// Minimal hand-rolled i18n. Picks language from localStorage on boot,
// stores the choice in a Zustand-like singleton, exposes `useT()` for
// components and `setLang()` for the toggle.
//
// Why not react-i18next: 100KB+ dep with features we don't need (lazy
// loading, plural rules, ICU formatting). Our table is one TS file with
// <500 keys, missing-key fallback is the key itself, no plurals beyond
// "1 view / N views" cases we can resolve inline.

import { useSyncExternalStore } from 'react';
import { TRANSLATIONS, DEFAULT_LANG, type Lang } from '../locales/translations';

// Two keys, by design:
//   al_lang       — the user's EXPLICIT choice (clicking the toggle). Wins
//                   over everything, forever.
//   al_lang_auto  — the IP-detected default, cached so we only hit the geo
//                   endpoint once per device. Used only when there's no
//                   explicit choice yet.
const STORAGE_KEY = 'al_lang';
const AUTO_KEY = 'al_lang_auto';

function read(key: string): Lang | null {
  try {
    const raw = localStorage.getItem(key);
    if (raw === 'zh' || raw === 'en') return raw;
  } catch { /* ignore */ }
  return null;
}

function readStored(): Lang {
  // explicit choice > cached IP default > app default
  return read(STORAGE_KEY) ?? read(AUTO_KEY) ?? DEFAULT_LANG;
}

/** True once the user has clicked the toggle at least once. */
export function hasExplicitLang(): boolean {
  return read(STORAGE_KEY) !== null;
}

let _lang: Lang = readStored();
const _listeners = new Set<() => void>();

function subscribe(cb: () => void) {
  _listeners.add(cb);
  return () => _listeners.delete(cb);
}
function getSnapshot() { return _lang; }

function emit(next: Lang) {
  if (next === _lang) return;
  _lang = next;
  _listeners.forEach(cb => cb());
}

export function setLang(next: Lang) {
  try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
  emit(next);
}

/** Apply an IP-detected default. No-op if the user already chose — their
 *  explicit pick must never be overridden by geo. Cached so the next load
 *  reads it synchronously (no flash, no re-fetch). */
export function applyAutoLang(next: Lang) {
  if (hasExplicitLang()) return;
  try { localStorage.setItem(AUTO_KEY, next); } catch { /* ignore */ }
  emit(next);
}

export function getLang(): Lang { return _lang; }

// `t(key)` returns the translated string for the active language, or the
// key itself if missing — half-migrated UIs show raw ids, which is easy
// to spot during the rollout.
export function useT() {
  const lang = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return (key: string): string => {
    const row = TRANSLATIONS[key];
    if (!row) {
      if (typeof console !== 'undefined') console.warn(`[i18n] missing key: ${key}`);
      return key;
    }
    return row[lang] ?? row[DEFAULT_LANG] ?? key;
  };
}

// Hook to read the active language for components that need it directly
// (e.g. the LangToggle, formatters that pick zh-CN vs en-US dates).
export function useLang(): Lang {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

// First-visit only: ask the backend (which reads Cloudflare's CF-IPCountry)
// which language to default to, and apply it. Skipped entirely once the
// user has an explicit choice OR a cached IP default — so it costs one tiny
// request per device, ever. Call once at app boot (main.tsx).
export async function detectLangFromIP(): Promise<void> {
  if (read(STORAGE_KEY) !== null || read(AUTO_KEY) !== null) return;
  try {
    const res = await fetch('/api/v2/geo', { headers: { Accept: 'application/json' } });
    if (!res.ok) return;
    const data = (await res.json()) as { lang?: string | null };
    if (data.lang === 'zh' || data.lang === 'en') applyAutoLang(data.lang);
  } catch { /* offline / blocked — keep the app default */ }
}
